import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@nexus/ui";

interface AnimatedHeightProps {
  children: ReactNode;
  className?: string;
}

/** Animates height changes when flex-wrapped or dynamic content resizes. */
export function AnimatedHeight({ children, className }: AnimatedHeightProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => setHeight(el.scrollHeight);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cn(
        "overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none",
        className,
      )}
      style={{ height: height ?? "auto" }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
